<template>
    <div class="redesign-realtime-user-table">
        <portal :to="`widget-header__${data.WidgetID}`">
            <div class="flex justify-start">
                <redesign-base-input
                    v-model="filteredTableInput"
                    prefix-icon="Search"
                    :placeholder="String(i18n.t('general.search'))"
                    class="mx-2"
                />
            </div>
        </portal>
        <RedesignDataTableComponent
            :columns="columns"
            :columns-config="columnsConfig"
            :table-data="searchedTableData"
            :row-key="getTableRowKey"
            :widget-data="data"
            :stripe="true"
            @update-widget="updateWidget"
            @update-columns-config="updateColumnsConfig"
            @update-headers-and-columns-config="updateColumnsAndHeadersConfig"
        >
            <template #cell="{ row, column }">
                <div>
                    <div v-if="column.prop === PropNames.CALL_INFO && row.extension ">
                        <div v-if="row.extension.calls.length">
                            <redesign-table-call-info
                                v-for="call in row.extension.calls"
                                :key="call.started"
                                :text-size="dynamicTableFontSize"
                                :call="call"
                                :class="{'mb-2' : row.extension.calls.length > 1}"
                            />
                        </div>
                        <div v-else>
                            {{ i18n.t('widget.table.noCallers') }}
                        </div>
                    </div>
                    <div v-else-if="column.prop === PropNames.STATUS">
                        <redesign-user-status
                            v-if="row.extension"
                            :text-size="dynamicTableFontSize"
                            :extension-status-data="row.statusData"
                            :extension="row.extension"
                        />
                        <div v-else>
                            ---
                        </div>
                    </div>
                    <div v-else-if="column.prop === PropNames.STATUS_DURATION">
                        <redesign-status-duration
                            v-if="row.extension"
                            :text-size="dynamicTableFontSize"
                            :extension="row.extension"
                            :settings="widgetSettings"
                            @recently-logged-out-expired="markExpired(getExtensionRowKey(row, row.extension))"
                        />
                    </div>
                    <span
                        v-else
                        v-html="row[column.prop]"
                    />
                </div>
            </template>
        </RedesignDataTableComponent>
    </div>
</template>


<script setup lang="ts">
import { computed, defineEmits, defineProps, ref } from 'vue'
import { getInitialExtensionTime } from '@/util/timeUtils'
import {
    isRecentlyLoggedOutEnabled,
    keepAfterExpiredKeys,
    shouldSkipExpiredLogout,
    useExpiredRecentlyLoggedOutKeys
} from '@/composables/useExpiredRecentlyLoggedOutKeys'
import { useRealTableExtension } from '@/composables/useRealTableExtension'
import { ExtensionStatusData, getExtensionStatusData } from '@/composables/useExtensionStatus'
import i18n from '@/i18n'
import { realTimeSettings } from '@/enum/defaultWidgetSettings'
import RedesignTableCallInfo from '@/components/RedesignComponents/RedesignTableComponents/RedesignTableCallInfo.vue'
import RedesignUserStatus from '@/components/RedesignComponents/RedesignTableComponents/RedesignUserStatus.vue'
import RedesignStatusDuration from '@/components/RedesignComponents/RedesignTableComponents/RedesignStatusDuration.vue'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignDataTableComponent from '@/components/RedesignBaseComponents/RedesignDataTableComponent.vue'
import {
    ColumnsObjectConfig,
    TableHeadersConfig,
    WidgetExtended,
    WidgetTemplateColumnWithConfig
} from '@/types/dashboard'
import { hasColumnSpecificAlignment } from '@/helpers/alignmentUtils'
import { Extension } from '@/types/extension'
import { PropNames } from '@/enum/realtimeTableConfig'
import cloneDeep from 'lodash/cloneDeep'
import { entitiesData } from '@/composables/globalData/useEntitiesData'
import { widgetTableContentFontSize } from '@/composables/globalData/useLayouts'
import { sortTableDataByDefaultList } from '@/helpers/tableSortingUtils'

type TableRow = Record<string, unknown>

type DefinedRowData = {
    online_user_id: number | string
    representant: string
    extension_id: number | string
    user_name: string
    extension_name: string
    status_duration: number
    status?: string
    statusData?: ExtensionStatusData
    extension: Extension
    caller_info: string
    call_info: number
}

type MappedRow = TableRow & DefinedRowData

interface CallAggregates {
    callerInfoWithName: string
    callerInfoPhoneOnly: string
    callInfo: number
}

interface ExtensionDerivedCache {
    statusData?: ExtensionStatusData
    statusSettingsRef?: Record<string, unknown>
    aggregates?: CallAggregates | null
}

interface MappedRowCacheEntry {
    extensionRef: Extension
    statusData?: ExtensionStatusData
    aggregates: CallAggregates | null
    noCallers: string
    mappedRow: MappedRow
}

function computeCallAggregates (extension: Extension): CallAggregates {
    let callerInfoWithName = ''
    let callerInfoPhoneOnly = ''
    let callInfo = 0
    for (const call of extension.calls) {
        callerInfoWithName += call.callername ? call.callername + '<br>' : call.callerphone + '<br>'
        callerInfoPhoneOnly += call.callerphone + '<br>'
        callInfo += Number(call.callStarted)
    }
    return {
        callerInfoWithName,
        callerInfoPhoneOnly,
        callInfo
    }
}

const props = withDefaults(
    defineProps<{
        columns: WidgetTemplateColumnWithConfig[]
        tableData: TableRow[]
        data: WidgetExtended
        columnsConfig: ColumnsObjectConfig
    }>(),
    {
        columns: () => [],
        tableData: () => [],
    }
)

const emit = defineEmits<{
    (event: 'update-widget', payload: WidgetExtended): void;
}>()
const filteredTableInput = ref('')
const derivedCacheByExtension = new WeakMap<Extension, ExtensionDerivedCache>()
let mappedRowCacheByKey = new Map<string, MappedRowCacheEntry>()
const {
    onlineUserIds,
    extensionsAllMapRef,
    extensionsOnlineMapRef
} = useRealTableExtension()

//Computed
const onlineUserIdsSet = computed(() => new Set(onlineUserIds.value))

const filteredTableData = computed(() => {
    if (!props.tableData.length || showLoggedOutUsers.value) {
        return props.tableData
    }
    return props.tableData.filter(item => typeof item.user_id === 'number' && onlineUserIdsSet.value.has(item.user_id))
})

const extensionsByUserIdIndex = computed<Map<number, Extension[]>>(() =>
    showLoggedOutUsers.value
        ? extensionsAllMapRef.value
        : extensionsOnlineMapRef.value
)

const usersById = computed<Map<number, string>>(() => {
    const map = new Map<number, string>()
    const users = entitiesData.value?.Users

    if (!users?.length) return map

    for (const user of users) {
        map.set(user.user_id, user.user_name)
    }
    return map
})

const noCallersText = computed(() => i18n.t('widget.table.noCallers') as string)

const mappedTableData = computed<(TableRow | MappedRow)[]>(() => {
    if (!filteredTableData.value?.length) {
        mappedRowCacheByKey = new Map<string, MappedRowCacheEntry>()
        return []
    }

    const extIndex = extensionsByUserIdIndex.value
    const settings = widgetSettings.value
    const noCallers = noCallersText.value
    const nextMappedRowCacheByKey = new Map<string, MappedRowCacheEntry>()

    const result = filteredTableData.value.reduce<(TableRow | MappedRow)[]>((acc, row) => {
        const extensionsList = typeof row.user_id === 'number'
            ? extIndex.get(row.user_id)
            : undefined

        if (!extensionsList?.length) {
            acc.push(row)
            return acc
        }

        for (const extension of extensionsList) {
            if (shouldSkipExpiredLogout(extension, settings)) {
                continue
            }

            const hasCalls = extension?.calls?.length > 0
            const statusData = getCachedStatusData(extension, settings)
            const aggregates = hasCalls ? getCachedCallAggregates(extension) : null
            const mappedRowCacheKey = getExtensionRowKey(row, extension)
            const cachedMappedRow = mappedRowCacheByKey.get(mappedRowCacheKey)
            
            if (
                cachedMappedRow &&
                cachedMappedRow.extensionRef === extension &&
                cachedMappedRow.statusData === statusData &&
                cachedMappedRow.aggregates === aggregates &&
                cachedMappedRow.noCallers === noCallers
            ) {
                nextMappedRowCacheByKey.set(mappedRowCacheKey, cachedMappedRow)
                acc.push(cachedMappedRow.mappedRow)
                continue
            }

            const mappedRow: MappedRow = {
                ...row,
                online_user_id: extension.onlineUserID || '--',
                representant: `${extension.userID} - ${extension.summery?.representative ?? '-'}`,
                extension_id: extension.number || '--',
                user_name: getUserName(extension.userID),
                extension_name: extension.userName || '--',
                status_duration: getInitialExtensionTime(extension, settings),
                extension,
                status: statusData?.text,
                statusData,
                caller_info: aggregates ? aggregates.callerInfoWithName : noCallers,
                call_info: aggregates?.callInfo ?? 0,
                caller_phone: aggregates ? aggregates.callerInfoPhoneOnly : noCallers
            }

            nextMappedRowCacheByKey.set(mappedRowCacheKey, {
                extensionRef: extension,
                statusData,
                aggregates,
                noCallers,
                mappedRow
            })
            acc.push(mappedRow)
        }

        return acc
    }, [])

    mappedRowCacheByKey = nextMappedRowCacheByKey
    return result
})

const sortedTableData = computed(() =>
    sortTableDataByDefaultList(mappedTableData.value, props.data)
)

const widgetSettings = computed(() => {
    const settings = props.data?.WidgetLayout?.settings
    return {
        ...realTimeSettings,
        ...(settings || {})
    }
})

const { expiredKeys, markExpired, clearExpired } = useExpiredRecentlyLoggedOutKeys(() => widgetSettings.value)

const searchedTableData = computed(() => {
    let data = sortedTableData.value

    if (isRecentlyLoggedOutEnabled(widgetSettings.value)) {
        data = data.filter((row: TableRow | MappedRow) =>
            keepAfterExpiredKeys(
                getTableRowKey(row),
                row.extension?.representativeStatus,
                expiredKeys.value,
                clearExpired
            )
        )
    }

    if (filteredTableInput.value) {
        const lowerFilter = filteredTableInput.value.toLowerCase()
        return data.filter((row) =>
            Object.values(row).some(field =>
                field?.toString().toLowerCase().includes(lowerFilter)
            )
        )
    }
    return data
})

const showLoggedOutUsers = computed(() => {
    return props.data.WidgetLayout?.settings?.showLoggedOutUsers
})

const dynamicTableFontSize = computed(() => {
    if (!props.data) {
        return widgetTableContentFontSize.value.fontSize
    }
    return props.data.WidgetLayout?.tableContentFontSize || widgetTableContentFontSize.value.fontSize
})

/*Methods*/
function getExtensionRowKey (row: TableRow, extension: Extension): string {
    return `${String(row.user_id ?? '')}:${extension.userID}:${extension.number}`
}

function getTableRowKey (row: TableRow | MappedRow): string {
  const extension = row?.extension as Extension | undefined
  return extension
      ? getExtensionRowKey(row, extension)
      : `user:${String(row.user_id ?? row.id ?? '')}`
}

function getOrCreateDerivedCache (extension: Extension): ExtensionDerivedCache {
    let cache = derivedCacheByExtension.get(extension)
    if (!cache) {
        cache = {}
        derivedCacheByExtension.set(extension, cache)
    }
    return cache
}

function getCachedStatusData (
    extension: Extension,
    settings: Record<string, unknown>
): ExtensionStatusData {
    const cache = getOrCreateDerivedCache(extension)
    if (cache.statusData && cache.statusSettingsRef === settings) {
        return cache.statusData
    }

    const value = getExtensionStatusData(extension, settings)
    cache.statusData = value
    cache.statusSettingsRef = settings
    return value
}

function getCachedCallAggregates (extension: Extension): CallAggregates | null {
    const cache = getOrCreateDerivedCache(extension)
    if (cache.aggregates !== undefined) {
        return cache.aggregates
    }

    const value = computeCallAggregates(extension)
    cache.aggregates = value
    return value
}

const getUserName = (id: number) => {
    return usersById.value.get(id) ?? '--'
}

const updateColumnsConfig = (config: ColumnsObjectConfig) => {
    if (!config || !Object.values(config)) {
        return
    }
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)
    const widgetData = cloneDeep(props.data)
    widgetData.WidgetLayout.columnsObjectConfig = config
    widgetData.WidgetLayout.hasColumnSpecificAlignment = hasColumnSpecificAlignmentValue

    updateWidget(widgetData)
}

const updateColumnsAndHeadersConfig = (config: ColumnsObjectConfig, tableHeadersConfig: TableHeadersConfig) => {
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)
    const widgetData = cloneDeep(props.data)
    if (config && Object.values(config)) {
        widgetData.WidgetLayout.columnsObjectConfig = config
    }
    if (tableHeadersConfig) {
        widgetData.WidgetLayout.tableHeadersConfig = tableHeadersConfig
    }
    widgetData.WidgetLayout.hasColumnSpecificAlignment = hasColumnSpecificAlignmentValue
    updateWidget(widgetData)
}

const updateWidget = (newData: WidgetExtended) => {
    emit('update-widget', newData)
}

</script>

<style lang="scss">
</style>
